<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { ref, inject, computed } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import type { Services } from "@services"
import type { UsageProfile } from "@modules/usage-profile/types"
import { useAuthStore } from "@core/stores/auth.store"
import { useBuildingStore } from "@core/stores/building-v2.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Profil d'utilisation",
            no_profile:
                "Aucun profil d'utilisation n'est associé à ce bâtiment.",
            action_button: "Modifier le profil {name}",
            confirm_delete: {
                title: "Supprimer le profil {name} ?",
                content:
                    "Le bâtiment n'aura plus de profil d'utilisation associé.",
                button: "Supprimer",
            },
        },
    },
})

// ================================
// PROPS
// ================================
const props = withDefaults(
    defineProps<{
        /** Affichage consultation uniquement (droits d’écriture). */
        readonly?: boolean
        /** Bâtiment en consultation seule (désactive les actions). */
        disabled?: boolean
    }>(),
    {
        readonly: false,
        disabled: false,
    }
)

// ================================
// EMITS
// ================================
const emit = defineEmits<{
    /** Profil mis à jour (création/modification) */
    "update:profile": [profile: UsageProfile]
    /** Profil supprimé */
    "remove:profile": []
}>()

const authStore = useAuthStore()
const buildingStore = useBuildingStore()
const { companyId } = storeToRefs(authStore)
const { building } = storeToRefs(buildingStore)

/** Bâtiment en cours (depuis le store) */
const getBuilding = computed(() => building.value ?? null)
/** Profil d'utilisation du bâtiment en cours */
const getProfile = computed(
    () => getBuilding.value?.profil_utilisation?._id_profilutilisation ?? null
)
/** Identifiant du bâtiment en cours */
const getBuildingId = computed(() => getBuilding.value?._id_building ?? "")
/** Nom du bâtiment en cours */
const getBuildingName = computed(() => getBuilding.value?.name ?? "")

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")!

// ================================
// STATE
// ================================
/** État d’entrée du dialog factory (edit | replace | '') */
const fromState = ref<string>("")
/** Visibilité du dialog de confirmation de suppression */
const isDeleteDialogVisible = ref(false)
/** Chargement en cours lors de la suppression */
const loadingDelete = ref(false)
/** Message d’erreur affiché après une suppression */
const errorDelete = ref("")
/** Visibilité du dialog factory (création/édition/remplacement) */
const isFactoryDialogVisible = ref(false)

// ================================
// COMPUTED
// ================================
/** Afficher le menu (même entreprise ou profil sans entreprise) */
const getShowMenu = computed(
    () =>
        !props.readonly &&
        !props.disabled &&
        (companyId.value === getProfile.value?._id_company ||
            !getProfile.value?._id_company)
)

// ================================
// INTERACTIONS
// ================================
/** Ouvre le dialog de confirmation de suppression */
function onClickRemove(): void {
    isDeleteDialogVisible.value = true
}

/** Supprime l’association côté API, retire le profil du store, puis émet remove:profile */
async function onConfirmDelete(): Promise<void> {
    const profile = getProfile.value
    const buildingId = getBuildingId.value
    if (!profile || !buildingId) return
    try {
        errorDelete.value = ""
        loadingDelete.value = true
        await services.$deleteUsageProfileBuilding({
            profileId: profile._id,
            buildingIds: [buildingId],
        })
        isDeleteDialogVisible.value = false
        buildingStore.removeBuildingUsageProfile()
        emit("remove:profile")
    } catch (err) {
        errorDelete.value = (err as Error).message
    } finally {
        loadingDelete.value = false
    }
}

/** Ouvre le dialog factory (création/édition/remplacement) */
function onClickActionButton(_toTarget?: string): void {
    fromState.value = getProfile.value ? "edit" : ""
    isFactoryDialogVisible.value = true
}

/** Ouvre le dialog factory en mode remplacement */
function onClickReplace(): void {
    fromState.value = "replace"
    isFactoryDialogVisible.value = true
}

/** Callback à la fin du formulaire (création/modification) */
function onDoneForm({ profile }: { profile: UsageProfile }): void {
    isFactoryDialogVisible.value = false

    // Met à jour dans le store le profil d'utilisation du bâtiment
    buildingStore.updateBuildingUsageProfile(profile)
}

/** Ferme le dialog factory et réinitialise l’état */
function onCloseFactory(): void {
    fromState.value = getProfile.value ? "edit" : ""
}
</script>

<template>
    <BaseTitledCard class="usage-profile-card">
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <BaseAppMessage
                    v-if="!getProfile"
                    severity="info"
                    :message="t('no_profile')"
                />
                <UsageProfileItem
                    v-if="getProfile"
                    :profile="getProfile"
                    :hide-menu="!getShowMenu"
                    hide-duplicate
                    hide-buildings
                    no-collapse
                    @click:see="() => {}"
                    @click:remove="onClickRemove"
                    @click:replace="onClickReplace"
                    @click:edit="onClickActionButton"
                    @edit:week="() => onClickActionButton('edit-week')"
                    @edit:exception="
                        () => onClickActionButton('edit-exception')
                    "
                />
                <BaseButton
                    v-if="getShowMenu"
                    severity="secondary"
                    class="mx-auto"
                    :label="
                        t('action_button', {
                            name: getProfile?.name ?? '',
                        })
                    "
                    @click="onClickActionButton"
                />

                <BaseAppMessage
                    v-if="errorDelete"
                    severity="error"
                    :message="errorDelete"
                />
            </div>
        </template>
    </BaseTitledCard>
    <!-- Dialog suppression -->
    <BaseDialog
        v-model:visible="isDeleteDialogVisible"
        modal
        :header="
            t('confirm_delete.title', {
                name: getProfile?.name ?? '',
            })
        "
        :closable="true"
        @hide="() => (isDeleteDialogVisible = false)"
    >
        <p class="text-center">
            {{ t("confirm_delete.content") }}
        </p>
        <template #footer>
            <BaseButton
                label="Annuler"
                text
                @click="isDeleteDialogVisible = false"
            />
            <BaseButton
                :label="t('confirm_delete.button')"
                severity="danger"
                :loading="loadingDelete"
                @click="onConfirmDelete"
            />
        </template>
        <BaseAppMessage
            v-if="errorDelete"
            severity="error"
            :message="errorDelete"
            class="mt-4"
        />
    </BaseDialog>

    <!-- Dialog factory (création/édition/remplacement) -->
    <UsageProfileDialogFactory
        v-model:visible="isFactoryDialogVisible"
        :company-id="companyId"
        :building-id="getBuildingId"
        :building-name="getBuildingName"
        :from-state="fromState"
        :profile="getProfile"
        @close="onCloseFactory"
        @done:form="onDoneForm"
    />
</template>
